import { cx } from './cx.js'
import styles from './Stamp.module.css'

export type StampVariant = 'ok' | 'warn' | 'stop' | 'you' | 'open'

/**
 * The FALLBACK label per variant. Deliberately not the only source any more.
 *
 * The original reasoning was right about drift — an overridable label drifts —
 * but it produced a worse bug than the one it prevented. `stop` is used for
 * both `mismatch` and `refused`, so a refused run was announced to a screen
 * reader as "Doesn't match — needs your review": the wrong verdict, stated
 * confidently, to the user least able to check it against the glyph.
 *
 * So a caller that knows the SERVED verdict label may pass it, and a caller
 * that does not gets the variant's own words. The drift the original comment
 * feared is not reintroduced, because the override is not a hand-typed string:
 * it comes from `GET /reference/verdicts`, which is generated from the
 * platform's `PlainLanguage` table.
 *
 * The fallbacks below describe the SEVERITY, not a verdict — `stop` means
 * "concluded unfavourably", which is true of both a mismatch and a refusal.
 * They used to be verdict wording, which is why `stop` could announce the
 * wrong one, and it is also why no verdict label now appears anywhere in a
 * component. That grep is CON-003's first Done-when item.
 */
const GLYPH: Record<StampVariant, string> = {
  ok: '[OK]',
  warn: '[!]',
  stop: '[X]',
  you: '[?]',
  // Deliberately not a mark. The other four say something happened;
  // this one says nothing has, and a glyph with weight would overstate it.
  open: '[ ]',
}

const ARIA_LABEL: Record<StampVariant, string> = {
  ok: 'Concluded favourably',
  warn: 'Needs your attention',
  stop: 'Concluded unfavourably',
  you: 'Needs a decision',
  open: 'Nothing has concluded',
}

export interface StampProps {
  variant: StampVariant
  /**
   * The served verdict label, when the caller has one. Never a literal typed
   * at the call site — see the note above `ARIA_LABEL`.
   */
  label?: string | undefined
}

export function Stamp({ variant, label }: StampProps) {
  return (
    <span
      className={cx(styles['stamp'], styles[variant])}
      aria-label={label ?? ARIA_LABEL[variant]}
    >
      {GLYPH[variant]}
    </span>
  )
}
